import React, { Suspense, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminBackButton from '../components/AdminBackButton';
import { AdminColumnPicker, ColumnDef, useColumnPrefs } from '../components/AdminColumnPicker';
import '../components/AdminLayout.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// The mailer module's one-by-one send button, loaded only when this page is opened.
const SendMailButton = React.lazy(() => import('../mailer/SendMailButton'));
const LastEmailedCell = React.lazy(() => import('../mailer/LastEmailedCell'));

const COLUMNS: ColumnDef[] = [
  { id: 'user', label: 'User', locked: true },
  { id: 'role', label: 'Role' },
  { id: 'plan', label: 'Plan' },
  { id: 'product', label: 'Product' },
  { id: 'joined', label: 'Joined' },
  { id: 'lastEmailed', label: 'Last emailed' },
];

interface AdminUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  role: string;
  plan: string | null;
  productEnabled: boolean;
  subscriptionPlanId: string | null;
  subscriptionPlanName?: string | null;
  createdAt: string;
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const columnPrefs = useColumnPrefs('admin.columns.users', COLUMNS);
  const show = columnPrefs.isVisible;

  const load = (q = '') => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '50' });
    if (q) params.set('search', q);
    fetch(`${API_BASE}/api/admin/users?${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setUsers(data.users || []); setTotal(data.total || 0); })
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    load(search);
  };

  return (
    <AdminLayout>
      <div className="admin-page-header admin-page-header--back">
        <div className="admin-page-title-row">
          <AdminBackButton />
          <h1 className="admin-page-title">Registered Users</h1>
        </div>
        <p className="admin-page-subtitle admin-page-subtitle--data">{total} total users</p>
      </div>
      <form onSubmit={handleSearch} className="admin-form-inline">
        <input
          className="admin-input admin-input-search"
          placeholder="Search by email or name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="admin-btn admin-btn-secondary" type="submit">Search</button>
      </form>
      {error && <div className="admin-error">{error}</div>}
      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <>
          <div className="admin-table-toolbar">
            <AdminColumnPicker columns={COLUMNS} prefs={columnPrefs} />
          </div>
          <div className="admin-card">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  {show('role') && <th>Role</th>}
                  {show('plan') && <th>Plan</th>}
                  {show('product') && <th>Product</th>}
                  {show('joined') && <th>Joined</th>}
                  {show('lastEmailed') && <th>Last emailed</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan={columnPrefs.count + 1} className="admin-cell-empty">No users found.</td></tr>
                )}
                {users.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div className="admin-cell-primary">{u.name || '—'}</div>
                      <div className="admin-cell-muted">{u.email}</div>
                    </td>
                    {show('role') && (
                      <td>
                        {u.role === 'super_admin' ? (
                          <span className="admin-badge admin-badge-admin">super_admin</span>
                        ) : (
                          <span className="admin-cell-muted">user</span>
                        )}
                      </td>
                    )}
                    {show('plan') && <td className="admin-cell-small">{u.subscriptionPlanName || u.plan || 'payg'}</td>}
                    {show('product') && (
                      <td>
                        <span className={`admin-badge ${u.productEnabled ? 'admin-badge-active' : 'admin-badge-archived'}`}>
                          {u.productEnabled ? 'enabled' : 'disabled'}
                        </span>
                      </td>
                    )}
                    {show('joined') && (
                      <td className="admin-cell-muted">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>
                    )}
                    {show('lastEmailed') && (
                      <td className="admin-cell-small">
                        <Suspense fallback="…"><LastEmailedCell source="registered" email={u.email} /></Suspense>
                      </td>
                    )}
                    <td>
                      <div className="admin-row-actions">
                        <Link to={`/admin/users/${u.id}`} className="admin-btn admin-btn-secondary">View</Link>
                        <Suspense fallback={null}>
                          <SendMailButton source="registered" id={u.id} email={u.email} />
                        </Suspense>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminUsers;
